import Badge from '@codegouvfr/react-dsfr/Badge';
import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import ToggleSwitch from '@codegouvfr/react-dsfr/ToggleSwitch';
import clsx from 'clsx';
import type { PrescriptionCounts } from 'maestro-shared/schema/Prescription/PrescriptionCounts';
import { hasDepartmentalEchelon } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanEchelon';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import type { ReactNode } from 'react';
import { useAuthentication } from 'src/hooks/useAuthentication';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import useWindowSize from 'src/hooks/useWindowSize';
import { t } from 'src/i18n';
import prescriptionsSlice from 'src/store/reducers/prescriptionsSlice';
import './ProgrammingPrescriptionList.scss';
import { pluralize } from 'src/utils/stringUtils';

interface Props {
  programmingPlans: ProgrammingPlanChecked[];
  counts?: PrescriptionCounts;
  exportURL: string;
  onImport?: () => void;
}

type ToggleFilterKey =
  | 'missingDistribution'
  | 'missingLaboratory'
  | 'withNovelty';

const ProgrammingPrescriptionListHeader = ({
  programmingPlans,
  counts,
  exportURL,
  onImport
}: Props) => {
  const dispatch = useAppDispatch();
  const { isMobile } = useWindowSize();
  const { hasRegionalView, hasDepartmentalView, hasUserPermission, userRole } =
    useAuthentication();

  const { prescriptionFilters } = useAppSelector(
    (state) => state.prescriptions
  );

  const isSamplerView = userRole === 'Sampler';
  const missingDistributionCount = counts?.missingDistributionCount ?? 0;
  const displayedMissingFinalizationCount =
    counts?.displayedMissingFinalizationCount ?? 0;

  const filterToggle = (
    key: ToggleFilterKey,
    label: string,
    inputTitle: string,
    count: number,
    testId?: string
  ): ReactNode => {
    const checked = prescriptionFilters[key] ?? false;
    return (
      <ToggleSwitch
        label={<span className="no-wrap">{`${label} (${count})`}</span>}
        inputTitle={inputTitle}
        checked={checked}
        disabled={count === 0 && !checked}
        onChange={(isChecked) =>
          dispatch(
            prescriptionsSlice.actions.changePrescriptionFilters({
              ...prescriptionFilters,
              [key]: isChecked
            })
          )
        }
        showCheckedHint={false}
        data-testid={testId}
      />
    );
  };

  return (
    <div
      className={clsx(
        cx('fr-mb-2w', 'fr-mb-md-5w', 'fr-container', 'fr-px-5w'),
        'prescription-list-header'
      )}
    >
      <div className="flex-grow-1">
        <h4 className={cx('fr-mb-1v')}>
          {counts
            ? `${t('subPlan', { count: counts.subPlanCount })} (${t('sample', {
                count: counts.sampleCount
              })})`
            : ''}
        </h4>
        {counts && (
          <Badge
            small
            severity={
              displayedMissingFinalizationCount > 0 ? 'warning' : 'success'
            }
          >
            {displayedMissingFinalizationCount > 0
              ? `${displayedMissingFinalizationCount} à finaliser`
              : pluralize(counts.displayedFinalizedCount, {
                  preserveCount: true
                })('finalisé')}
          </Badge>
        )}
      </div>
      <div className="prescription-list-header__filters">
        {!isSamplerView &&
          filterToggle(
            'missingDistribution',
            'Répartition à réaliser',
            'Filtrer les lignes avec répartition à réaliser',
            missingDistributionCount
          )}
        {hasUserPermission('updatePrescriptionLaboratories') &&
          (hasDepartmentalView ||
            (hasRegionalView &&
              programmingPlans.some(
                (plan) => !hasDepartmentalEchelon(plan.distributionKind)
              ))) &&
          filterToggle(
            'missingLaboratory',
            'Laboratoires à attribuer',
            'Filtrer les lignes avec laboratoire à attribuer',
            counts?.missingLaboratoryCount ?? 0,
            'missing-laboratory-toggle'
          )}
        {filterToggle(
          'withNovelty',
          'Nouveautés',
          'Filtrer les lignes présentant des nouveautés',
          counts?.noveltyCount ?? 0
        )}
      </div>
      <Button
        iconId="fr-icon-file-download-line"
        priority="secondary"
        onClick={() => window.open(exportURL)}
        title="Exporter"
        size={isMobile ? 'small' : 'medium'}
      >
        Exporter
      </Button>
      {onImport && (
        <Button
          iconId="fr-icon-upload-line"
          priority="secondary"
          onClick={onImport}
          title="Importer"
          size={isMobile ? 'small' : 'medium'}
        >
          Importer
        </Button>
      )}
    </div>
  );
};

export default ProgrammingPrescriptionListHeader;
